/**
 * AI verification client.
 *
 * Scoped under a run. Three entry points (single-row, selection,
 * all-filtered) all hit the same start-stream endpoint with a
 * different ``match_ids`` array. The browser opens the SSE stream
 * with fetch + ReadableStream so the POST body carries the
 * action + scope on the same request that opens the stream.
 */

import { api, csrfHeaders } from "@/api/client";


export type ScopeKind = "single" | "selection" | "all";


export interface AgentActionMeta {
  id:             string;
  label:          string;
  description:    string;
  scope_kinds:    ScopeKind[];
  evaluators:     string[];
  min_candidates: number;
}


export interface AgentEvent {
  type: string;
  // The full payload as emitted by the eval-agent runner. Shape
  // varies per .type; consumers narrow downstream.
  [k: string]: unknown;
}


export interface VerifySessionListing {
  session_id: string;
  started_at: string | null;
  ended_at:   string | null;
  action_id:  string | null;
  scope_size: number;
  outcome:    string | null;
}


export interface VerifySession {
  session_id: string;
  run_id:     string;
  events:     AgentEvent[];
  verdicts:   Array<Record<string, unknown>>;
}


export interface StartRequest {
  action_id:   string;
  match_ids?:  string[];
  /** When true, skip the eval-agent verdict cache so Gemini is hit
   *  fresh on every candidate. Default false — repeated runs over the
   *  same scope serve from cache in milliseconds. */
  override_cache?: boolean;
  tier_model?: string;
}


/** Open the SSE stream for one verification session.
 *
 * Cancel by calling the returned cancel() — this aborts the fetch,
 * which propagates to uvicorn → the eval-agent subprocess gets a
 * SIGTERM. So "cancel" really stops Gemini calls.
 */
export function streamVerification(
  runId: string, req: StartRequest,
): {
  events: AsyncIterableIterator<AgentEvent>;
  cancel: () => void;
} {
  const controller = new AbortController();
  const events = (async function* (): AsyncIterableIterator<AgentEvent> {
    const res = await fetch(`/api/runs/${runId}/ai-verify/start-stream`, {
      method:      "POST",
      credentials: "include",
      headers:     { "Content-Type": "application/json", ...csrfHeaders("POST") },
      body:        JSON.stringify(req),
      signal:      controller.signal,
    });
    if (!res.ok) {
      let detail = res.statusText;
      try { detail = (await res.json()).detail ?? detail; } catch { /* ignore */ }
      throw new Error(detail);
    }
    if (!res.body) throw new Error("No SSE body");
    const reader  = res.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buf = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep = buf.indexOf("\n\n");
      while (sep >= 0) {
        const frame = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        const ev = parseFrame(frame);
        if (ev) yield ev;
        sep = buf.indexOf("\n\n");
      }
    }
  })();
  return {
    events,
    cancel: () => controller.abort(),
  };
}


function parseFrame(frame: string): AgentEvent | null {
  let type = "message";
  let data = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith(": ")) continue;
    const colon = line.indexOf(":");
    if (colon < 0) continue;
    const field = line.slice(0, colon);
    const value = line.slice(colon + 1).replace(/^ /, "");
    if      (field === "event") type = value;
    else if (field === "data")  data = value;
  }
  if (!data) return null;
  try {
    return { type, ...(JSON.parse(data) as Record<string, unknown>) };
  } catch {
    return { type, raw: data };
  }
}


export interface VerdictsPage {
  total: number;
  offset: number;
  limit: number;
  verdicts: AgentEvent[];
  counts: {
    pass: number;
    partial: number;
    fail: number;
    abstain: number;
  };
}

export interface VerdictsParams {
  q?: string;
  overall?: "pass" | "full" | "partial" | "fail" | "abstain";
  limit?: number;
  offset?: number;
}

export const AiVerify = {
  listActions: (runId: string, scopeKind: ScopeKind) =>
    api.get<AgentActionMeta[]>(
      `/runs/${runId}/ai-verify/actions?scope_kind=${scopeKind}`,
    ),
  listSessions: (runId: string) =>
    api.get<VerifySessionListing[]>(`/runs/${runId}/ai-verify/sessions`),
  session: (runId: string, sessionId: string) =>
    api.get<VerifySession>(`/runs/${runId}/ai-verify/sessions/${sessionId}`),
  /** Server-side filtered + paginated verdict results. */
  results: (runId: string, params?: VerdictsParams): Promise<VerdictsPage> => {
    const segments: string[] = [];
    if (params?.q) segments.push(`q=${encodeURIComponent(params.q)}`);
    if (params?.overall) segments.push(`overall=${params.overall}`);
    if (params?.limit !== undefined) segments.push(`limit=${params.limit}`);
    if (params?.offset !== undefined) segments.push(`offset=${params.offset}`);
    const qs = segments.length > 0 ? `?${segments.join("&")}` : "";
    return api.get<VerdictsPage>(`/runs/${runId}/ai-verify/results${qs}`);
  },
  /** Trigger a browser download of all filtered verdicts (CSV or JSON). */
  exportUrl: (
    runId: string,
    format: "csv" | "json",
    params?: Pick<VerdictsParams, "q" | "overall">,
  ): string => {
    const segments: string[] = [`format=${format}`];
    if (params?.q) segments.push(`q=${encodeURIComponent(params.q)}`);
    if (params?.overall) segments.push(`overall=${params.overall}`);
    return `/api/runs/${runId}/ai-verify/export?${segments.join("&")}`;
  },
};
